import { Component, inject } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { Event, NavigationEnd, NavigationStart, RouterOutlet } from '@angular/router';

import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { filter, switchMap } from 'rxjs';

import { CamDeviceInfoService } from '@camelot/capacitor';
import { NotificationBoxComponent } from '@camelot/notification';
import { CAM_AUTH_TOKEN } from '@camelot/user';
import { isNonNullable } from '@camelot/utils';

import { environment } from '../environments/environment';
import { BaseComponent } from './features/core/abstract/base-component';
import { AppTranslationService } from './services/translation.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [NotificationBoxComponent, RouterOutlet],
})
export class AppComponent extends BaseComponent {
  private readonly _authService = inject(CAM_AUTH_TOKEN);
  private readonly _deviceInfoService = inject(CamDeviceInfoService);
  private readonly _gtmService = inject(GoogleTagManagerService);
  private readonly _dialogService = inject(MatDialog);
  private readonly _bottomSheetService = inject(MatBottomSheet);

  constructor() {
    super();

    this._authService.load();
    AppTranslationService.getInstance();
    this.setBodyClasses();
    this.fetchCurrentUser();

    this._router.events.subscribe((event: Event) => {
      if (event instanceof NavigationStart) {
        window.scrollTo(0, 0);
        this._dialogService.closeAll();
        this._bottomSheetService.dismiss();
        return;
      }
      if (event instanceof NavigationEnd) {
        this.pushPageView(event.url);
      }
    });
  }

  /**
   * The current user carries the tenant informations every tenant-scoped query depends on
   * (CMS contents, Strapi sales, ...). It used to be fetched by `app-header`, which is no longer
   * rendered on first-level layouts — so it is fetched here, once the session is authenticated.
   */
  private fetchCurrentUser() {
    this._registerSubscription(
      this._authService.user$
        .pipe(
          filter(isNonNullable),
          switchMap(() => this._usersService.fetchCurrentUser$())
        )
        .subscribe()
    );
  }

  /**
   * `pushTag` rejects when no container id is configured — which is every environment but
   * production. Left unhandled that raised an unhandled rejection on each navigation.
   */
  private pushPageView(url: string) {
    if (!environment.GTM) {
      return;
    }

    this._gtmService.pushTag({ event: 'page', pageName: url }).catch(() => undefined);
  }

  private setBodyClasses() {
    this._deviceInfoService.deviceClasses$.subscribe(classes => {
      for (let classToAdd of classes.filter(x => x.length > 0)) document.body.classList.add(classToAdd);
    });
  }
}
